import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, FileText, Search as SearchIcon, Building2, Layers, ClipboardCheck, X } from "lucide-react";

const TABS = [
  { id: "offices", label: "Offices", icon: Building2 },
  { id: "categories", label: "Categories", icon: Layers },
  { id: "services", label: "Services & Documents", icon: ClipboardCheck },
];

export default function SiteAdmin() {
  const [tab, setTab] = useState("services");
  return (
    <div className="space-y-4" data-testid="site-admin-page">
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <Button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`h-9 text-xs font-semibold ${tab === t.id ? "bg-[color:var(--brand-navy)] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              data-testid={`tab-${t.id}`}
            >
              <Icon className="h-4 w-4 mr-1.5" /> {t.label}
            </Button>
          );
        })}
      </div>
      {tab === "offices" && <OfficesTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "services" && <ServicesTab />}
    </div>
  );
}

// -------------------- OFFICES --------------------
function OfficesTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => setRows((await api.get("/site/offices")).data);
  useEffect(() => { load(); }, []);
  const save = async (o) => {
    try {
      if (o.id) await api.put(`/site/offices/${o.id}`, o);
      else await api.post("/site/offices", o);
      toast.success("Office saved");
      setEdit(null); await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete office "${o.name}"?`)) return;
    await api.delete(`/site/offices/${o.id}`); toast.success("Deleted"); await load();
  };
  const move = async (o, dir) => {
    await api.put(`/site/offices/${o.id}`, { order: (o.order || 0) + dir }); await load();
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="font-display font-bold text-slate-900">Office Branches</div>
        <Button onClick={() => setEdit({ name: "", is_head: false, address: "", phone_display: "", phone_tel: "", whatsapp: "", email: "", website_url: "", office_timings: "", maps_url: "", order: rows.length, active: true })} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="add-office-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Office
        </Button>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((o) => (
          <div key={o.id} className="p-4 flex items-start gap-3">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(o, -1)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(o, 1)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900">{o.name}</div>
                {o.is_head && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">HEAD OFFICE</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${o.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{o.active ? "ACTIVE" : "INACTIVE"}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">{o.address}</div>
              <div className="text-xs text-slate-500 mt-1">{o.phone_display} · {o.email} · {o.office_timings}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEdit(o)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(o)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No offices yet. Add your first branch.</div>}
      </div>
      {edit && <OfficeDialog row={edit} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}

function OfficeDialog({ row, onClose, onSave }) {
  const [f, setF] = useState(row);
  const patch = (k, v) => setF({ ...f, [k]: v });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{row.id ? "Edit Office" : "Add Office"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => patch("name", e.target.value)} data-testid="office-name" /></Field>
          <Field label="Office Timings"><Input value={f.office_timings} onChange={(e) => patch("office_timings", e.target.value)} placeholder="Mon–Sat 10:00–20:00" /></Field>
          <Field label="Address (full)" className="md:col-span-2"><Textarea rows={2} value={f.address} onChange={(e) => patch("address", e.target.value)} /></Field>
          <Field label="Phone (display)"><Input value={f.phone_display} onChange={(e) => patch("phone_display", e.target.value)} /></Field>
          <Field label="Phone (tel, digits only)"><Input value={f.phone_tel} onChange={(e) => patch("phone_tel", e.target.value)} /></Field>
          <Field label="WhatsApp (with country code)"><Input value={f.whatsapp} onChange={(e) => patch("whatsapp", e.target.value)} placeholder="919209463396" /></Field>
          <Field label="Email"><Input value={f.email} onChange={(e) => patch("email", e.target.value)} /></Field>
          <Field label="Website URL"><Input value={f.website_url} onChange={(e) => patch("website_url", e.target.value)} /></Field>
          <Field label="Google Maps URL" className="md:col-span-2"><Input value={f.maps_url} onChange={(e) => patch("maps_url", e.target.value)} placeholder="https://www.google.com/maps/…" /></Field>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#C89B4A] mb-2">Public Site Branding (only used when Head Office)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Brand Line 1 (large)"><Input value={f.brand_line1 || ""} onChange={(e) => patch("brand_line1", e.target.value)} placeholder="SHREE SAMARTH" data-testid="office-brand-line1" /></Field>
            <Field label="Brand Line 2 (small)"><Input value={f.brand_line2 || ""} onChange={(e) => patch("brand_line2", e.target.value)} placeholder="Consultancy" data-testid="office-brand-line2" /></Field>
            <Field label="Tagline" className="md:col-span-2"><Input value={f.tagline || ""} onChange={(e) => patch("tagline", e.target.value)} placeholder="Legal Documentation · Property Registration · Government Services" /></Field>
            <Field label="Brand Description (hero + footer intro)" className="md:col-span-2"><Textarea rows={3} value={f.brand_description || ""} onChange={(e) => patch("brand_description", e.target.value)} placeholder="Professional legal documentation services with complete transparency…" /></Field>
            <Field label="Copyright Text" className="md:col-span-2"><Input value={f.copyright_text || ""} onChange={(e) => patch("copyright_text", e.target.value)} placeholder="© 2026 Shree Samarth Consultancy. All rights reserved." /></Field>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.is_head} onCheckedChange={(v) => patch("is_head", v)} /> Head Office (brand source)</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.active} onCheckedChange={(v) => patch("active", v)} /> Active</label>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-slate-200 hover:bg-slate-300 text-slate-800">Cancel</Button>
          <Button onClick={() => onSave(f)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-office-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, className, children }) {
  return <div className={`space-y-1 ${className || ""}`}><Label className="text-[10px] font-semibold uppercase tracking-wider">{label}</Label>{children}</div>;
}

// -------------------- CATEGORIES --------------------
function CategoriesTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => setRows((await api.get("/site/categories")).data);
  useEffect(() => { load(); }, []);
  const save = async (c) => {
    try {
      if (c.id) await api.put(`/site/categories/${c.id}`, c);
      else await api.post("/site/categories", c);
      toast.success("Category saved"); setEdit(null); await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Its services will be deactivated.`)) return;
    await api.delete(`/site/categories/${c.id}`); toast.success("Deleted"); await load();
  };
  const move = async (c, dir) => { await api.put(`/site/categories/${c.id}`, { order: (c.order || 0) + dir }); await load(); };
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="font-display font-bold text-slate-900">Service Categories</div>
        <Button onClick={() => setEdit({ name: "", tagline: "", description: "", order: rows.length, active: true })} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Category
        </Button>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((c) => (
          <div key={c.id} className="p-4 flex items-start gap-3">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(c, -1)} className="p-1 rounded hover:bg-slate-100 text-slate-500"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(c, 1)} className="p-1 rounded hover:bg-slate-100 text-slate-500"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900">{c.name}</div>
                <span className="text-[10px] text-slate-400 font-mono">{c.slug}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "ACTIVE" : "INACTIVE"}</span>
              </div>
              {c.tagline && <div className="text-xs text-slate-600 mt-1 font-semibold">{c.tagline}</div>}
              {c.description && <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEdit(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(c)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <Dialog open onOpenChange={() => setEdit(null)}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle>{edit.id ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="category-name" /></Field>
              <Field label="Tagline"><Input value={edit.tagline} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} /></Field>
              <Field label="Description"><Textarea rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Active</label>
            </div>
            <DialogFooter>
              <Button onClick={() => setEdit(null)} className="bg-slate-200 hover:bg-slate-300 text-slate-800">Cancel</Button>
              <Button onClick={() => save(edit)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-category-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// -------------------- SERVICES + DOCS --------------------
function ServicesTab() {
  const [cats, setCats] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  const [docSearch, setDocSearch] = useState("");
  const [docHits, setDocHits] = useState(null);
  const load = async () => {
    const [cs, ss] = await Promise.all([
      api.get("/site/categories"),
      api.get("/site/services", { params: { category_id: catFilter, q: q || undefined } }),
    ]);
    setCats(cs.data); setRows(ss.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [catFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => {
    if (!docSearch.trim()) { setDocHits(null); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/site/documents/search", { params: { q: docSearch } });
      setDocHits(data);
    }, 300);
    return () => clearTimeout(t);
  }, [docSearch]);
  const save = async (s) => {
    try {
      if (s.id) await api.put(`/site/services/${s.id}`, s);
      else await api.post("/site/services", s);
      toast.success("Service saved"); setEdit(null); await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"? Its documents will also be removed.`)) return;
    await api.delete(`/site/services/${s.id}`); toast.success("Deleted"); await load();
  };
  const move = async (s, dir) => { await api.put(`/site/services/${s.id}`, { order: (s.order || 0) + dir }); await load(); };
  const catName = (cid) => (cats.find((c) => c.id === cid)?.name || "");
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3">
        <div className="relative">
          <SearchIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search service by name" className="h-9 pl-8 w-64" data-testid="service-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="h-9 w-56" data-testid="service-cat-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">— All Categories —</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <SearchIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search required document…" className="h-9 pl-8 w-64" data-testid="doc-search" />
        </div>
        <div className="flex-1" />
        <Button onClick={() => setEdit({ name: "", category_id: cats[0]?.id || "", icon: "FileText", description: "", special_instructions: "", order: rows.length, active: true })} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Service
        </Button>
      </div>

      {docHits && (
        <div className="bg-white border-2 border-amber-400 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Document search matches ({docHits.length})</div>
            <button onClick={() => { setDocSearch(""); setDocHits(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
          </div>
          <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
            {docHits.map((d) => <div key={d.id} className="flex items-center gap-2"><FileText className="h-3 w-3 text-slate-500" /><span className="font-semibold">{d.name}</span><span className="text-slate-500">— {d.service_name}</span>{d.mandatory && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">MANDATORY</span>}</div>)}
            {docHits.length === 0 && <div className="text-slate-400">No matches</div>}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
            <tr><th className="px-3 py-2 text-left w-16">Order</th><th className="px-3 py-2 text-left">Service</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-center">Docs</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-center w-32">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((s) => (
              <tr key={s.id} data-testid="service-row">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <span className="font-mono-num text-xs w-6">{s.order}</span>
                    <button onClick={() => move(s, -1)} className="p-0.5 rounded hover:bg-slate-100"><ArrowUp className="h-3 w-3" /></button>
                    <button onClick={() => move(s, 1)} className="p-0.5 rounded hover:bg-slate-100"><ArrowDown className="h-3 w-3" /></button>
                  </div>
                </td>
                <td className="px-3 py-2 font-semibold text-slate-900">{s.name}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{catName(s.category_id)}</td>
                <td className="px-3 py-2 text-center">
                  <Button onClick={() => setDocsFor(s)} className="h-7 text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid={`manage-docs-${s.id}`}>
                    Manage Documents
                  </Button>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? "ACTIVE" : "INACTIVE"}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => setEdit(s)} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(s)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No services yet</div>}
      </div>

      {edit && <ServiceDialog row={edit} categories={cats} onClose={() => setEdit(null)} onSave={save} />}
      {docsFor && <DocsDialog service={docsFor} onClose={() => setDocsFor(null)} />}
    </div>
  );
}

function ServiceDialog({ row, categories, onClose, onSave }) {
  const [f, setF] = useState(row);
  const patch = (k, v) => setF({ ...f, [k]: v });
  const iconOptions = ["FileText", "Home", "Building2", "ClipboardCheck", "Landmark", "Briefcase", "Globe", "KeyRound", "ShieldCheck"];
  const useDefaults = f.use_company_defaults !== false; // default true
  const validPhone = (v) => !v || /^(\+?91)?[6-9]\d{9}$/.test(v.replace(/\s+/g, ""));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{row.id ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Service Name" className="md:col-span-2"><Input value={f.name} onChange={(e) => patch("name", e.target.value)} data-testid="service-name" /></Field>
          <Field label="Category">
            <Select value={f.category_id} onValueChange={(v) => patch("category_id", v)}>
              <SelectTrigger data-testid="service-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Icon (lucide)">
            <Select value={f.icon} onValueChange={(v) => patch("icon", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{iconOptions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Short Description" className="md:col-span-2"><Textarea rows={3} value={f.description} onChange={(e) => patch("description", e.target.value)} /></Field>
          <Field label="Special Instructions" className="md:col-span-2"><Textarea rows={2} value={f.special_instructions} onChange={(e) => patch("special_instructions", e.target.value)} /></Field>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#C89B4A]">Service Contact Details</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm md:col-span-2 bg-slate-50 rounded p-2">
              <Switch checked={useDefaults} onCheckedChange={(v) => patch("use_company_defaults", v)} data-testid="svc-use-default" />
              <span>Use company default contact details (phone, WhatsApp, email from Head Office)</span>
            </label>
            <label className="flex items-center gap-2 text-sm md:col-span-2 bg-slate-50 rounded p-2">
              <Switch checked={!!f.show_contact_on_public} onCheckedChange={(v) => patch("show_contact_on_public", v)} data-testid="svc-show-contact" />
              <span>Show contact details on the public service page</span>
            </label>
            <Field label="Primary Mobile"><Input value={f.primary_phone || ""} onChange={(e) => patch("primary_phone", e.target.value)} placeholder="+91 9xxxxxxxxx" data-testid="svc-primary-phone" /></Field>
            <Field label="Alternate Mobile"><Input value={f.alt_phone || ""} onChange={(e) => patch("alt_phone", e.target.value)} placeholder="+91 9xxxxxxxxx" data-testid="svc-alt-phone" /></Field>
            <Field label="WhatsApp (with country code)"><Input value={f.whatsapp || ""} onChange={(e) => patch("whatsapp", e.target.value)} placeholder="919xxxxxxxxx" data-testid="svc-whatsapp" /></Field>
            <Field label="Email"><Input value={f.email || ""} onChange={(e) => patch("email", e.target.value)} placeholder="service@example.com" data-testid="svc-email" /></Field>
          </div>
          {!useDefaults && (
            <div className="mt-2 text-[11px] text-slate-500">
              {!validPhone(f.primary_phone) && <div className="text-red-600">Primary mobile: enter a valid Indian mobile (with optional +91).</div>}
              {!validPhone(f.alt_phone) && <div className="text-red-600">Alternate mobile: enter a valid Indian mobile (with optional +91).</div>}
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm mt-3"><Switch checked={!!f.active} onCheckedChange={(v) => patch("active", v)} /> Active on Public Site</label>
        <DialogFooter>
          <Button onClick={onClose} className="bg-slate-200 hover:bg-slate-300 text-slate-800">Cancel</Button>
          <Button onClick={() => onSave(f)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-service-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocsDialog({ service, onClose }) {
  const [docs, setDocs] = useState([]);
  const [f, setF] = useState({ name: "", mandatory: true, note: "", order: 0 });
  const [editing, setEditing] = useState(null);
  // Local mirror of the service's contact fields — persisted to the service itself
  const [svc, setSvc] = useState({
    use_company_defaults: service.use_company_defaults !== false,
    show_contact_on_public: !!service.show_contact_on_public,
    primary_phone: service.primary_phone || "",
    alt_phone: service.alt_phone || "",
    whatsapp: service.whatsapp || "",
    email: service.email || "",
  });
  const [savingSvc, setSavingSvc] = useState(false);
  const load = async () => setDocs((await api.get(`/site/services/${service.id}/documents`)).data);
  useEffect(() => { load(); }, []);
  const validPhone = (v) => !v || /^(\+?91)?[\s-]?[6-9]\d{9}$/.test(v.replace(/\s+/g, ""));
  const saveSvcContact = async () => {
    // Validate phones whenever a value is present — regardless of the "use company defaults" toggle
    if (svc.primary_phone && !validPhone(svc.primary_phone)) return toast.error("Primary mobile is invalid");
    if (svc.alt_phone && !validPhone(svc.alt_phone)) return toast.error("Alternate mobile is invalid");
    setSavingSvc(true);
    try {
      await api.put(`/site/services/${service.id}`, svc);
      toast.success("Service contact details saved");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Save failed"); }
    finally { setSavingSvc(false); }
  };
  const add = async () => {
    if (!f.name.trim()) return toast.error("Document name required");
    await api.post(`/site/services/${service.id}/documents`, { ...f, order: docs.length });
    setF({ name: "", mandatory: true, note: "", order: 0 }); await load();
  };
  const saveEdit = async () => {
    await api.put(`/site/documents/${editing.id}`, editing);
    setEditing(null); await load();
  };
  const del = async (d) => {
    if (!window.confirm(`Delete document "${d.name}"?`)) return;
    await api.delete(`/site/documents/${d.id}`); await load();
  };
  const move = async (d, dir) => { await api.put(`/site/documents/${d.id}`, { order: (d.order || 0) + dir }); await load(); };
  const useDefaults = svc.use_company_defaults;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Required Documents — {service.name}</DialogTitle></DialogHeader>

        {/* ---- Service Contact Details (Problem 2 — user request) ---- */}
        <div className="border-2 border-[#C89B4A]/40 rounded-md p-3 mb-3 bg-amber-50/40 space-y-2" data-testid="docs-contact-panel">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#C89B4A]">Service Contact Details</div>
            <Button onClick={saveSvcContact} disabled={savingSvc} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="save-svc-contact-btn">
              {savingSvc ? "Saving…" : "Save Contact"}
            </Button>
          </div>
          <label className="flex items-center gap-2 text-xs bg-white rounded p-2 border border-slate-200">
            <Switch checked={useDefaults} onCheckedChange={(v) => setSvc({ ...svc, use_company_defaults: v })} data-testid="docs-use-default" />
            <span>Use company default contact details (Head Office phone / WhatsApp / email)</span>
          </label>
          <label className="flex items-center gap-2 text-xs bg-white rounded p-2 border border-slate-200">
            <Switch checked={svc.show_contact_on_public} onCheckedChange={(v) => setSvc({ ...svc, show_contact_on_public: v })} data-testid="docs-show-contact" />
            <span>Show contact details on public service page</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Primary Mobile"><Input value={svc.primary_phone} onChange={(e) => setSvc({ ...svc, primary_phone: e.target.value })} placeholder="+91 9xxxxxxxxx" className="h-9" data-testid="docs-primary-phone" /></Field>
            <Field label="Alternate Mobile"><Input value={svc.alt_phone} onChange={(e) => setSvc({ ...svc, alt_phone: e.target.value })} placeholder="+91 9xxxxxxxxx" className="h-9" data-testid="docs-alt-phone" /></Field>
            <Field label="WhatsApp (with country code)"><Input value={svc.whatsapp} onChange={(e) => setSvc({ ...svc, whatsapp: e.target.value })} placeholder="919xxxxxxxxx" className="h-9" data-testid="docs-whatsapp" /></Field>
            <Field label="Email"><Input value={svc.email} onChange={(e) => setSvc({ ...svc, email: e.target.value })} placeholder="service@example.com" className="h-9" data-testid="docs-email" /></Field>
          </div>
          {useDefaults && (svc.primary_phone || svc.alt_phone || svc.whatsapp || svc.email) && (
            <div className="text-[11px] text-slate-500 bg-slate-100 rounded p-1.5">
              <strong>Note:</strong> "Use company defaults" is ON — the values above are saved to the DB but the public page will show the Head Office contact. Turn the toggle off to make the service-specific values live.
            </div>
          )}
          {!useDefaults && (
            <div className="text-[11px] space-y-0.5">
              {svc.primary_phone && !validPhone(svc.primary_phone) && <div className="text-red-600">Primary mobile: enter a valid Indian mobile (with optional +91).</div>}
              {svc.alt_phone && !validPhone(svc.alt_phone) && <div className="text-red-600">Alternate mobile: enter a valid Indian mobile (with optional +91).</div>}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Document name (e.g. Aadhaar Card of both parties)" data-testid="doc-name-input" />
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Optional note or special instruction" />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.mandatory} onCheckedChange={(v) => setF({ ...f, mandatory: v })} /> Mandatory</label>
              <Button onClick={add} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="add-doc-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Document</Button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {docs.map((d) => (
              <div key={d.id} className="p-2 flex items-center gap-2 text-sm" data-testid="doc-row">
                <button onClick={() => move(d, -1)} className="p-0.5 rounded hover:bg-slate-100"><ArrowUp className="h-3 w-3" /></button>
                <button onClick={() => move(d, 1)} className="p-0.5 rounded hover:bg-slate-100"><ArrowDown className="h-3 w-3" /></button>
                {editing?.id === d.id ? (
                  <div className="flex-1 flex gap-2">
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-8 text-xs" />
                    <label className="flex items-center gap-1 text-xs"><Switch checked={editing.mandatory} onCheckedChange={(v) => setEditing({ ...editing, mandatory: v })} /> M</label>
                    <Button onClick={saveEdit} className="h-8 bg-emerald-600 text-white text-xs">Save</Button>
                    <Button onClick={() => setEditing(null)} className="h-8 bg-slate-200 text-slate-800 text-xs">Cancel</Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <span className="font-semibold">{d.name}</span>
                      {d.note && <span className="text-slate-500 text-xs italic ml-2">— {d.note}</span>}
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${d.mandatory ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{d.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
                    <button onClick={() => setEditing(d)} className="p-1 rounded hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(d)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            ))}
            {docs.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No documents yet — add the first one above.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-slate-200 hover:bg-slate-300 text-slate-800">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
